import { useState } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import AssistanceFooter from 'components/Common/AssistanceFooter';
import Modal from 'components/Common/Modal';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import AnnouncementGroupAutocomplete from 'components/Announcemnet/AnnouncementGroupAutocomplete';
import { useForm } from 'react-hook-form';
import CardWrapper from 'components/Common/CardWrapper';
import { ANNOUNCEMENTS, NewAnnouncement, SendAnnouncementProps } from 'types/announcements';
import { sendAnnouncement } from 'redux/slices/announcements/sendAnnouncementSlice';
import Routes from 'constants/routes';
import { setAnnouncementsTab } from 'redux/slices/common/appSlice';
import MuiLoader from 'components/Common/Loader';
import BasketsAutocomplete from 'components/Common/autocomplete/BasketsAutocomplete';
import { Basket } from 'types/common';

const ANNOUNCEMENT_TITLE = 'Make An Announcement';
const ANNOUNCEMENT_DESCRIPTION =
    'Fill in the appropriate fields and send a notification out to your community. Once a notification has been sent it cannot be undone. Make sure your information is correct before sending.';

export default function SendAnnouncement({ defaultValues }: SendAnnouncementProps) {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id } = useParams();
    const [modalOpen, setModalOpen] = useState(false);
    const [announcementSentModal, setAnnouncementSentModal] = useState(false);
    const { loading, announcement } = useSelector((state: RootState) => ({
        loading: state.announcement.sendAnnouncement.loading,
        announcement: state.announcement.announcementDetails,
    }));
    const { loading: getAnnouncementLoading } = announcement || {};
    const groupId = defaultValues?.data?.announcementGroupId;

    const { setValue, watch, getValues, reset } = useForm<NewAnnouncement>({ defaultValues });

    const handleConfirm = async () => {
        const formValues = getValues();
        if (!formValues?.data?.announcementGroupId || !formValues?.companyId) {
            return;
        }
        const { payload }: any = await dispatch(sendAnnouncement(formValues));
        if (payload?.code === 200) {
            setAnnouncementSentModal(true);
            setModalOpen(false);
            reset();
        }
    };

    const goToAnnouncements = () => {
        dispatch(setAnnouncementsTab(ANNOUNCEMENTS));
        navigate(Routes.announcements.main);
    };

    const handleBasketChange = (basket: Basket) => {
        const { id, name } = basket || {};
        setValue('data.event', { id, name });
    };

    return (
        <CardWrapper stackProps={{ alignItems: 'center' }}>
            <MuiLoader absolute loading={getAnnouncementLoading} />
            <Stack maxWidth={1016}>
                <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                    {ANNOUNCEMENT_TITLE}
                </Typography>
                <Divider />
                <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                    {ANNOUNCEMENT_DESCRIPTION}
                </Typography>
                <Stack mx={{ md: 8 }}>
                    <AnnouncementGroupAutocomplete
                        defaultValue={groupId ? String(groupId) : undefined}
                        setGroup={(group) => setValue('data.announcementGroupId', group?.id)}
                    />
                    <TextInput
                        value={watch('title')}
                        onChange={({ target }) => setValue('title', target.value)}
                        label="Title"
                        icon={null}
                        width="100%"
                        placeholder="Type announcement title"
                        sx={{ mt: 2 }}
                    />
                    <TextInput
                        value={watch('message')}
                        onChange={({ target }) => setValue('message', target.value)}
                        label="Message"
                        icon={null}
                        multiline
                        minRows={6}
                        maxRows={6}
                        width="100%"
                        placeholder="Type announcement message here"
                        sx={{ mt: 2, mb: 18, p: 0 }}
                    />
                    <BasketsAutocomplete onChange={(_, value) => handleBasketChange(value)} />
                </Stack>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button width={372} onClick={() => setModalOpen(true)}>
                        {id ? 'Resend' : 'Send'}
                    </Button>
                </Box>

                {/* Confirm Modal */}
                <Modal onClose={() => setModalOpen(false)} isOpen={modalOpen} modalTitle={`Confirm ${id ? 'Resend' : 'Send'}`}>
                    <>
                        <Typography sx={{ fontSize: 18, my: 2 }}>
                            {`Are you sure you want to ${id ? 'resend' : 'send'} this announcement?`}
                        </Typography>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                                marginTop: 32,
                            }}
                        >
                            <Button backgroundColor="white" onClick={() => setModalOpen(false)} width={'45%'} title="No" />
                            <Button loading={loading} onClick={handleConfirm} width={'45%'} title="Yes" />
                        </div>
                    </>
                </Modal>

                {/* Announcement Sent Modal */}
                <Modal
                    isOpen={announcementSentModal}
                    onClose={() => setAnnouncementSentModal(false)}
                    modalTitle={`"Announcement ${id ? 'Resent' : 'Sent'}!`}
                >
                    <Typography sx={{ fontSize: 18, my: 2 }}>
                        Your announcement has been sent to the group selected, you may view it in the Announcements section of this portal.{' '}
                        <br />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                                marginTop: 32,
                            }}
                        >
                            <Button onClick={goToAnnouncements} width={'80%'} title="Go to Announcements" />
                        </div>
                    </Typography>
                </Modal>

                <AssistanceFooter />
            </Stack>
        </CardWrapper>
    );
}
